import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Chip,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  promotionsService,
  productsService,
  brandsService,
  familiesService,
  categoriesService,
} from '../services/api';

const PromotionEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNewPromotion = !id;

  const [loading, setLoading] = useState(!isNewPromotion);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Resources pour les sélections
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [families, setFamilies] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    type: 'percentage',
    discountValue: '',
    buyQuantity: '',
    getQuantity: '',
    minimumPurchase: '',
    maximumDiscount: '',
    targetType: 'all',
    targetIds: [],
    maxUses: '',
    maxUsesPerUser: '',
    startDate: '',
    endDate: '',
    isActive: true,
    autoApply: false,
    priority: 0,
    isCombinable: false,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger les ressources nécessaires
      const [productsRes, brandsRes, familiesRes, categoriesRes] = await Promise.all([
        productsService.getAll(),
        brandsService.getAll(),
        familiesService.getAll(),
        categoriesService.getAll(),
      ]);

      setProducts(productsRes.data);
      setBrands(brandsRes.data);
      setFamilies(familiesRes.data);
      setCategories(categoriesRes.data);

      // Si édition, charger la promotion
      if (!isNewPromotion) {
        const promoRes = await promotionsService.getById(id);
        const promo = promoRes.data;

        setFormData({
          name: promo.name || '',
          code: promo.code || '',
          description: promo.description || '',
          type: promo.type || 'percentage',
          discountValue: promo.discountValue || '',
          buyQuantity: promo.buyQuantity || '',
          getQuantity: promo.getQuantity || '',
          minimumPurchase: promo.minimumPurchase || '',
          maximumDiscount: promo.maximumDiscount || '',
          targetType: promo.targetType || 'all',
          targetIds: promo.targetIds || [],
          maxUses: promo.maxUses || '',
          maxUsesPerUser: promo.maxUsesPerUser || '',
          startDate: promo.startDate ? promo.startDate.slice(0, 16) : '',
          endDate: promo.endDate ? promo.endDate.slice(0, 16) : '',
          isActive: promo.isActive !== undefined ? promo.isActive : true,
          autoApply: promo.autoApply || false,
          priority: promo.priority || 0,
          isCombinable: promo.isCombinable || false,
        });
      }
    } catch (err) {
      console.error('Erreur lors du chargement:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const dataToSend = {
        ...formData,
        discountValue: formData.discountValue ? parseFloat(formData.discountValue) : null,
        buyQuantity: formData.buyQuantity ? parseInt(formData.buyQuantity) : null,
        getQuantity: formData.getQuantity ? parseInt(formData.getQuantity) : null,
        minimumPurchase: formData.minimumPurchase ? parseFloat(formData.minimumPurchase) : null,
        maximumDiscount: formData.maximumDiscount ? parseFloat(formData.maximumDiscount) : null,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        maxUsesPerUser: formData.maxUsesPerUser ? parseInt(formData.maxUsesPerUser) : null,
        priority: parseInt(formData.priority),
      };

      if (isNewPromotion) {
        await promotionsService.create(dataToSend);
      } else {
        await promotionsService.update(id, dataToSend);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/promotions');
      }, 1500);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde de la promotion');
    } finally {
      setSaving(false);
    }
  };

  const getTargetOptions = () => {
    switch (formData.targetType) {
      case 'product':
        return products;
      case 'brand':
        return brands;
      case 'family':
        return families;
      case 'category':
        return categories;
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Accueil
        </Link>
        <Link component={RouterLink} to="/promotions" underline="hover" color="inherit">
          Promotions
        </Link>
        <Typography color="text.primary">
          {isNewPromotion ? 'Nouvelle promotion' : 'Modifier la promotion'}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        {isNewPromotion ? 'Créer une promotion' : 'Modifier la promotion'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Promotion {isNewPromotion ? 'créée' : 'modifiée'} avec succès !
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Informations générales
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Nom de la promotion"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Code promo"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="Ex: NOEL2025"
                helperText="Laissez vide pour auto-application"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Type de réduction
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="percentage">Pourcentage</MenuItem>
                  <MenuItem value="fixed">Montant fixe</MenuItem>
                  <MenuItem value="buy_x_get_y">X achetés, Y offerts</MenuItem>
                  <MenuItem value="free_shipping">Livraison gratuite</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.type === 'percentage' && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Pourcentage de réduction"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Grid>
            )}

            {formData.type === 'fixed' && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Montant de réduction"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            )}

            {formData.type === 'buy_x_get_y' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Quantité à acheter"
                    value={formData.buyQuantity}
                    onChange={(e) => setFormData({ ...formData, buyQuantity: e.target.value })}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Quantité offerte"
                    value={formData.getQuantity}
                    onChange={(e) => setFormData({ ...formData, getQuantity: e.target.value })}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Montant minimum d'achat"
                value={formData.minimumPurchase}
                onChange={(e) => setFormData({ ...formData, minimumPurchase: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Réduction maximale"
                value={formData.maximumDiscount}
                onChange={(e) => setFormData({ ...formData, maximumDiscount: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Optionnel - Plafonne la réduction"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Ciblage
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Type de ciblage</InputLabel>
                <Select
                  value={formData.targetType}
                  label="Type de ciblage"
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value, targetIds: [] })}
                >
                  <MenuItem value="all">Tous les produits</MenuItem>
                  <MenuItem value="product">Produit(s) spécifique(s)</MenuItem>
                  <MenuItem value="brand">Marque(s)</MenuItem>
                  <MenuItem value="family">Famille(s)</MenuItem>
                  <MenuItem value="category">Catégorie(s)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.targetType !== 'all' && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={getTargetOptions()}
                  getOptionLabel={(option) => option.name || option.title || ''}
                  value={getTargetOptions().filter((opt) => formData.targetIds.includes(opt.id))}
                  onChange={(e, newValue) => {
                    setFormData({ ...formData, targetIds: newValue.map((item) => item.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Sélectionner les ${formData.targetType === 'product' ? 'produits' : formData.targetType === 'brand' ? 'marques' : formData.targetType === 'family' ? 'familles' : 'catégories'}`}
                      placeholder="Rechercher..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || option.title}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ))
                  }
                />
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Limites et dates
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Utilisations maximales"
                value={formData.maxUses}
                onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                inputProps={{ min: 1 }}
                helperText="Laissez vide pour illimité"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max. par utilisateur"
                value={formData.maxUsesPerUser}
                onChange={(e) => setFormData({ ...formData, maxUsesPerUser: e.target.value })}
                inputProps={{ min: 1 }}
                helperText="Laissez vide pour illimité"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Priorité"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                helperText="Plus élevé = appliqué en premier"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Date de début"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Date de fin"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Options
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Promotion active"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoApply}
                    onChange={(e) => setFormData({ ...formData, autoApply: e.target.checked })}
                  />
                }
                label="Appliquer automatiquement (sans code)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isCombinable}
                    onChange={(e) => setFormData({ ...formData, isCombinable: e.target.checked })}
                  />
                }
                label="Combinable avec d'autres promotions"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : isNewPromotion ? 'Créer' : 'Enregistrer'}
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/promotions')}>
              Annuler
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default PromotionEdit;





















